// src/features/reits-ranking/api.ts
//
// Data fetcher for the REITs (Real-Estate equities) ranking page.
// Real-Estate sibling of features/stock-ranking/api.ts and
// features/etf-ranking/api.ts. Talks to the FastAPI reits-ranking router
// via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/reits-ranking router is reached as an
//     authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/reits-ranking/top-assets` path here —
// adding `/api/v1` would double the prefix. See api/client.ts
// (REQUIRED_API_PREFIX).

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape from GET /api/v1/reits-ranking/top-assets
// and /universe. The REIT scorer emits the SAME column set as the equity
// scorer, so the row mirrors the backend contract in
// app/core/cache_keys.py (_TOP_SIGNALS_ROW_KEYS) and the ReitsDailyRanking
// ORM model exactly: `sector`, `fundamental_z_score` (Quality) and the
// composite `forge_ultra_signal`. Keep them in lock-step.
export interface ReitsRankingRow {
    symbol: string;
    sector: string;
    forge_ultra_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    universe_rank: number;
    sector_rank: number;
}

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: ReitsRankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    // Resolves to GET /api/v1/reits-ranking/top-assets (prefix from baseURL).
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/reits-ranking/top-assets',
    );
    return data;
};

// --- Full universe (all ranked REITs, non-paginated) --------------------
// The backend returns the entire cross-section for the latest date in one
// payload. We sort it entirely client-side, so column-sort clicks never
// hit the network again. Unlike the ETF router, the REIT /universe takes
// no filter param (the universe is a single "Real Estate" sector).

// A universe row carries the same public columns as a leaderboard row.
export type UniverseRow = ReitsRankingRow;

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (): Promise<UniverseResponse> => {
    // Resolves to GET /api/v1/reits-ranking/universe (prefix from baseURL).
    const { data } = await apiClient.get<UniverseResponse>(
        '/reits-ranking/universe',
    );
    return data;
};

// --- Batched historical trajectories (sparklines) ------------------------
// One request for many symbols collapses what would be an N+1 fetch
// waterfall (one call per visible table row) into a single round-trip.

export interface HistoryPoint {
    date: string;
    forge_ultra_signal: number;
}

// Map of {symbol: [{date, forge_ultra_signal}, ...]}. Every requested
// symbol is present as a key (empty array if it has no rows in window).
export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/reits-ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Sector summary (factor aggregate) -----------------------------------
// Per-sector average factor z-scores, aggregated server-side over the
// latest date. The REIT universe is a single "Real Estate" sector, so this
// typically returns ONE row — it drives the factor-averages summary panel
// rather than a multi-spoke radar. The contract is identical to the equity
// /sector-summary so the shape generalises if the universe ever spans more.

export interface SectorSummaryRow {
    sector: string;
    count: number;
    avg_momentum_z: number;
    avg_fundamental_z: number;
    avg_sentiment_z: number;
    avg_signal: number;
}

export interface SectorSummaryResponse {
    as_of_date: string;
    sectors: SectorSummaryRow[];
}

export const fetchSectorSummary = async (): Promise<SectorSummaryResponse> => {
    // Resolves to GET /api/v1/reits-ranking/sector-summary.
    const { data } = await apiClient.get<SectorSummaryResponse>(
        '/reits-ranking/sector-summary',
    );
    return data;
};

// --- Quick stats (symbol pop-over) ---------------------------------------
// Latest close price + company name + sector for a single REIT symbol.
// Powers the click-to-open symbol pop-over on the universe table. We go
// through the shared `apiClient` (NOT raw fetch) so the WP JWT is attached —
// the /reits-ranking router is auth-gated, so an unauthenticated fetch
// would 401.
//
// `sector` and `last_price` are nullable: a recently-listed name can have
// pricing but no profile row (or vice versa), in which case the backend
// returns null and the UI renders "N/A".

export interface QuickStats {
    symbol: string;
    company_name: string;
    sector: string | null;
    last_price: number | null;
}

export const fetchQuickStats = async (
    symbol: string,
): Promise<QuickStats> => {
    // Resolves to GET /api/v1/reits-ranking/quick-stats/<symbol>.
    const { data } = await apiClient.get<QuickStats>(
        `/reits-ranking/quick-stats/${encodeURIComponent(symbol)}`,
    );
    return data;
};

// --- Per-symbol detail pop-over fetchers (ReitsDetailPopup) -------------
// Single-symbol trailing-window series sourced from the
// /reits-ranking/{price-history,factors,signal,overview} endpoints. The
// three series endpoints are Redis-cached server-side and always return
// {symbol, count, series} (series may be empty -> render an empty-state,
// never a 404 unless the nightly chain has never run). The price series is
// raw OHLCV from the shared asset_daily_pricing table; the factor / signal
// series come from reits_daily_rankings.

export interface PriceBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number | null;
}

export interface PriceHistoryResponse {
    symbol: string;
    count: number;
    series: PriceBar[];
}

export const fetchPriceHistory = async (
    symbol: string,
    days = 365,
): Promise<PriceHistoryResponse> => {
    const { data } = await apiClient.get<PriceHistoryResponse>(
        `/reits-ranking/price-history/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface FactorPoint {
    date: string;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    annualized_volatility: number;
    forge_ultra_signal: number;
}

export interface FactorsResponse {
    symbol: string;
    count: number;
    series: FactorPoint[];
}

export const fetchFactors = async (
    symbol: string,
    days = 365,
): Promise<FactorsResponse> => {
    const { data } = await apiClient.get<FactorsResponse>(
        `/reits-ranking/factors/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface SignalPoint {
    date: string;
    forge_ultra_signal: number;
}

export interface SignalResponse {
    symbol: string;
    count: number;
    series: SignalPoint[];
}

export const fetchSignal = async (
    symbol: string,
    days = 365,
): Promise<SignalResponse> => {
    const { data } = await apiClient.get<SignalResponse>(
        `/reits-ranking/signal/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

// One-shot latest snapshot for the popup header stats (no chart). Returns
// 404 for a symbol not in the latest ranked cross-section, so callers should
// treat its failure as best-effort (the charts must still render).
export interface OverviewResponse {
    symbol: string;
    as_of_date: string;
    sector: string;
    universe_rank: number;
    sector_rank: number;
    forge_ultra_signal: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    annualized_volatility: number;
}

export const fetchOverview = async (
    symbol: string,
): Promise<OverviewResponse> => {
    const { data } = await apiClient.get<OverviewResponse>(
        `/reits-ranking/overview/${encodeURIComponent(symbol)}`,
    );
    return data;
};
